import { useState } from "react";
import { Link } from "react-router-dom";
import type { BookingWithVehicle } from "@/types";
import { formatKES, vehicleTitle } from "@/lib/format";
import { formatLongDate } from "@/lib/date";
import { countDaysLabel, isAwaitingPayment, isCancelled } from "@/lib/bookings";
import BookingStatusBadge from "@/components/base/BookingStatusBadge";
import DamageFlagBadge from "@/components/base/DamageFlagBadge";

const FALLBACK_IMAGE =
  "https://readdy.ai/api/search-image?query=Studio%20automotive%20photography%20of%20a%20sleek%20silhouette%20car%20on%20a%20seamless%20charcoal%20grey%20background%20with%20soft%20reflective%20floor%20dramatic%20softbox%20lighting%20premium%20editorial%20advertisement%20style&width=1200&height=800&seq=gari-placeholder-01&orientation=landscape";

interface BookingRequestCardProps {
  request: BookingWithVehicle;
  onUpdate: (bookingId: number, status: string) => Promise<void>;
  flagged?: boolean;
}

export default function BookingRequestCard({ request, onUpdate, flagged = false }: BookingRequestCardProps) {
  const [busy, setBusy] = useState<null | string>(null);
  const [error, setError] = useState("");

  const vehicle = request.vehicle;
  const image = vehicle?.images?.[0]?.url || FALLBACK_IMAGE;
  const title = vehicle ? vehicleTitle(vehicle.make, vehicle.model, vehicle.year) : "Your car";
  const pending = isAwaitingPayment(request.status);
  const cancelled = isCancelled(request.status);

  async function handle(status: string) {
    setBusy(status);
    setError("");
    try {
      await onUpdate(request.id, status);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update this booking.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <article className="rounded-2xl surface-card p-4 sm:p-5">
      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="h-28 w-full shrink-0 overflow-hidden rounded-xl bg-background-100 sm:h-24 sm:w-36">
          <img src={image} alt={`${title} booking request`} title={`${title} — booking request`} className="h-full w-full object-cover object-top" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wider text-foreground-500">Request #{request.id}</p>
              <h3 className="mt-1 truncate font-heading text-base font-semibold text-foreground-950">{title}</h3>
              <p className="mt-1 flex items-center gap-1.5 text-sm text-foreground-600">
                <i className="ri-user-3-line text-foreground-500"></i>
                <span className="truncate">{request.renter_name || "A Gari rider"}</span>
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <BookingStatusBadge status={request.status} />
              {flagged && <DamageFlagBadge />}
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-calendar-line text-primary-500"></i>
              {formatLongDate(request.start_date)} → {formatLongDate(request.end_date)}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-time-line text-primary-500"></i> {countDaysLabel(request.days)}
            </span>
            <span className="inline-flex items-center gap-1.5 font-medium text-foreground-900">
              <i className="ri-wallet-3-line text-primary-500"></i> {formatKES(request.total_price)}
            </span>
          </div>

          {error && (
            <p className="mt-3 flex items-center gap-2 rounded-lg border border-secondary-700/40 bg-secondary-950/40 px-3 py-2 text-xs text-secondary-300">
              <i className="ri-error-warning-line"></i> {error}
            </p>
          )}

          {pending && !cancelled ? (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => handle("confirmed")}
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 disabled:opacity-50"
              >
                {busy === "confirmed" ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
                Accept
              </button>
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => handle("cancelled")}
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-900 transition-colors hover:border-secondary-500/60 disabled:opacity-50"
              >
                {busy === "cancelled" ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-close-line"></i>}
                Decline
              </button>
              <Link
                to={`/booking/${request.id}`}
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-900 transition-colors hover:border-primary-500/60"
              >
                <i className="ri-chat-3-line"></i> Message rider
              </Link>
              {vehicle && (
                <Link
                  to={`/vehicle/${vehicle.id}`}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-2 text-sm text-foreground-600 transition-colors hover:text-foreground-950"
                >
                  <i className="ri-roadster-line"></i> View car
                </Link>
              )}
            </div>
          ) : (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-500">
                {cancelled
                  ? "You declined this request."
                  : "Accepted — the rider has been notified and payment is in progress."}
              </span>
              <Link
                to={`/booking/${request.id}`}
                className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-medium text-primary-500 transition-colors hover:text-primary-400"
              >
                <i className="ri-chat-3-line"></i> Message rider
              </Link>
              {vehicle && (
                <Link
                  to={`/vehicle/${vehicle.id}`}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-medium text-primary-500 transition-colors hover:text-primary-400"
                >
                  <i className="ri-roadster-line"></i> View car
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  );
}